import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, ClipboardList, ShoppingBag, X } from 'lucide-react';
import { getProducts, nivelStock, porComprar, NIVEL, Product } from '../../../services/productsApi';
import { getVitrinaStatus, guardarConteo, getHistorialConteos, getConteo, ConteoDiff, ConteoDetalle, UltimoConteo, VitrinaStatus, EVENTO_VITRINA, avisarVitrina } from '../vitrinaApi';
import { Skeleton } from '../../../components/Skeleton';
import { useToast } from '../../../context/ToastContext';
import { useRefrescoAlVolver } from '../../../hooks/useRefrescoAlVolver';

/** Mientras recepción siga vendiendo la tiendita en Kydemy, esas ventas no descuentan aquí: lo
 *  que «falta» en un conteo es sobre todo venta hecha allá. Cuando se deje Kydemy, a `false`. */
const SE_VENDE_TAMBIEN_EN_KYDEMY = true;

const etiqueta = (p: Product) => (p.brand ? `${p.brand} — ${p.name}` : p.name);
const fechaLarga = (iso: string) => new Date(iso).toLocaleString('es-MX', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

/** «faltan 4» / «sobran 2» */
export function textoDiferencia(d: number) {
  const n = Math.abs(d);
  return d < 0 ? `falta${n === 1 ? '' : 'n'} ${n}` : `sobra${n === 1 ? '' : 'n'} ${n}`;
}

/** CONTEO DE VITRINA. A ciegas: se escribe lo que hay sin ver lo que el sistema espera, y la
 *  diferencia se enseña después de guardar. Renglón vacío = «no se contó» (no es «hay 0»). */
export function ConteoVitrinaModal({ movidos, turno, onClose, onSaved }: {
  movidos: number[];
  turno?: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [products, setProducts] = useState<Product[] | null>(null);
  const [vals, setVals] = useState<Record<string, string>>({});
  const [verTodos, setVerTodos] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resultado, setResultado] = useState<{ contados: number; diffs: ConteoDiff[]; low: { name: string; stock: number; level: 'rojo' | 'ambar'; buy: number | null }[] } | null>(null);

  useEffect(() => { getProducts().then((r) => setProducts(r.products)).catch(() => setProducts([])); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { primero, resto } = useMemo(() => {
    const set = new Set(movidos.map(String));
    const all = products || [];
    const a = all.filter((p) => set.has(p.id));
    // Sin nada «movido» (primer conteo, o turno sin ventas) se enseña la vitrina entera.
    return a.length ? { primero: a, resto: all.filter((p) => !set.has(p.id)) } : { primero: all, resto: [] as Product[] };
  }, [products, movidos]);

  const contados = Object.values(vals).filter((v) => v !== '').length;
  const total = (products || []).length;

  async function guardar() {
    const lines = Object.entries(vals).filter(([, v]) => v !== '').map(([id, v]) => ({ product_id: Number(id), counted: Number(v) }));
    if (!lines.length) { toast.error('Cuenta al menos un producto'); return; }
    setSaving(true);
    try {
      const r = await guardarConteo(lines);
      // Semáforo con lo recién contado: qué quedó en rojo o ámbar (rojos primero).
      const low = r.lines.map((l) => {
        const p = (products || []).find((x) => Number(x.id) === l.product_id);
        const nivel = p ? nivelStock({ ...p, stock: l.counted }) : null;
        return p && (nivel === 'rojo' || nivel === 'ambar') ? { name: l.name, stock: l.counted, level: nivel, buy: porComprar({ ...p, stock: l.counted }) } : null;
      }).filter((x): x is NonNullable<typeof x> => !!x).sort((a, b) => (a.level === b.level ? a.stock - b.stock : a.level === 'rojo' ? -1 : 1));
      setResultado({ contados: r.lines_counted, diffs: r.lines.filter((l) => l.diff != null && l.diff !== 0), low });
      avisarVitrina();
      onSaved();
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar el conteo'); }
    finally { setSaving(false); }
  }

  const renglon = (p: Product) => (
    <li key={p.id} className="flex items-center gap-3 px-3 py-2">
      {p.photoUrl ? (
        <img src={p.photoUrl} alt="" loading="lazy" className="h-10 w-10 shrink-0 rounded-lg border border-line object-cover" />
      ) : (
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line bg-surface-2 text-content-3"><ShoppingBag size={16} /></span>
      )}
      <label htmlFor={`cv-${p.id}`} className="min-w-0 flex-1">
        <span className="block truncate text-sm text-content">{etiqueta(p)}</span>
        {p.category && <span className="block truncate text-xs text-content-3">{p.category}</span>}
      </label>
      <input id={`cv-${p.id}`} data-testid="conteo-input" data-product={p.id} value={vals[p.id] ?? ''} inputMode="numeric" placeholder="—"
        onChange={(e) => setVals((v) => ({ ...v, [p.id]: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
        className="w-20 rounded-lg border border-line bg-surface px-2 py-2 text-center text-sm tabular-nums text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30" />
    </li>
  );

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label="Conteo de vitrina">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div data-testid="conteo-vitrina" className="modal-panel relative flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-2xl border border-line bg-surface shadow-xl sm:rounded-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 font-display text-lg font-bold text-content"><ClipboardList size={18} className="text-brand-blue" /> Conteo de vitrina</h3>
            {!resultado && (
              <p className="mt-0.5 text-xs text-content-3">
                {turno ? `Turno ${turno}. ` : ''}Escribe cuántas piezas hay de cada producto. Lo que no cuentes, déjalo vacío.
              </p>
            )}
          </div>
          <button onClick={onClose} className="shrink-0 text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>

        {resultado ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4" data-testid="conteo-resultado">
            <p className="flex items-center gap-2 text-sm font-semibold text-content">
              <CheckCircle2 size={18} className="text-emerald-500" />
              Conteo guardado · {resultado.contados} producto{resultado.contados === 1 ? '' : 's'}
            </p>
            {resultado.diffs.length === 0 ? (
              <p className="mt-2 text-sm text-content-2">Todo lo contado coincide con lo que el sistema esperaba.</p>
            ) : (
              <>
                <p className="mt-3 text-xs font-medium uppercase tracking-wide text-content-3">No coincidían ({resultado.diffs.length})</p>
                <ul className="mt-1 divide-y divide-line rounded-xl border border-line">
                  {resultado.diffs.map((d) => (
                    <li key={d.product_id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <span className="min-w-0 truncate text-content">{d.name}</span>
                      <span className="shrink-0 text-right text-xs text-content-3">
                        esperaba {d.expected} · contaste {d.counted}
                        <b className={`ml-2 text-sm ${d.diff! < 0 ? 'text-amber-600 dark:text-amber-400' : 'text-brand-blue'}`}>{textoDiferencia(d.diff!)}</b>
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs text-content-3">
                  Las existencias ya quedaron en lo que contaste.
                  {SE_VENDE_TAMBIEN_EN_KYDEMY && ' Mientras la tiendita se venda también en Kydemy, lo que falta suele ser venta hecha allá, no pérdida.'}
                </p>
              </>
            )}
            {resultado.low.length > 0 && (
              <div data-testid="conteo-por-surtir">
                <p className="mt-4 text-xs font-medium uppercase tracking-wide text-content-3">Por surtir ({resultado.low.length})</p>
                <ul className="mt-1 divide-y divide-line rounded-xl border border-line">
                  {resultado.low.map((l) => (
                    <li key={l.name} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${NIVEL[l.level].punto}`} />
                      <span className="min-w-0 flex-1 truncate text-content">{l.name}</span>
                      <span className="shrink-0 text-xs text-content-3">quedan <b className={`text-sm ${NIVEL[l.level].texto}`}>{l.stock}</b>{l.buy ? ` · comprar ${l.buy}` : ''}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : products == null ? (
          <div className="space-y-2 px-5 py-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : total === 0 ? (
          <p className="px-5 py-6 text-sm text-content-3">No hay productos a la venta que contar.</p>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            {resto.length > 0 && (
              <p className="border-b border-line bg-surface-2 px-5 py-1.5 text-xs font-medium uppercase tracking-wide text-content-3">
                Se movieron desde el último conteo ({primero.length})
              </p>
            )}
            <ul className="divide-y divide-line px-2">{primero.map(renglon)}</ul>
            {resto.length > 0 && (verTodos ? (
              <>
                <p className="border-y border-line bg-surface-2 px-5 py-1.5 text-xs font-medium uppercase tracking-wide text-content-3">Los demás ({resto.length})</p>
                <ul className="divide-y divide-line px-2">{resto.map(renglon)}</ul>
              </>
            ) : (
              <button onClick={() => setVerTodos(true)} data-testid="conteo-ver-todos"
                className="w-full border-t border-line px-5 py-3 text-left text-sm font-medium text-brand-blue transition hover:bg-surface-2">
                Contar también los demás ({resto.length})
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3 border-t border-line px-5 py-3">
          {resultado ? (
            <button onClick={onClose} className="ml-auto rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">Listo</button>
          ) : (
            <>
              <span className="text-xs tabular-nums text-content-3">{contados} de {total} contados</span>
              <div className="ml-auto flex gap-2">
                <button onClick={onClose} className="whitespace-nowrap rounded-lg border border-line bg-surface px-3 py-2 text-sm font-medium text-content-2 transition hover:bg-surface-2">Después</button>
                <button onClick={guardar} disabled={saving || contados === 0}
                  className="whitespace-nowrap rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
                  {saving ? 'Guardando…' : 'Guardar conteo'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>, document.body);
}

/** HISTORIAL DE CONTEOS: cuándo, quién, turno, cuántos productos y cuántas diferencias. Un conteo se
 *  abre para ver su detalle (diferencias primero). Es el registro que pidió Carlos (29-sep-2026). */
export function HistorialConteosModal({ onClose }: { onClose: () => void }) {
  const [lista, setLista] = useState<UltimoConteo[] | null>(null);
  const [abierto, setAbierto] = useState<ConteoDetalle | null>(null);
  const [cargando, setCargando] = useState<number | null>(null);
  useEffect(() => { getHistorialConteos().then((r) => setLista(r.data)).catch(() => setLista([])); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') (abierto ? setAbierto(null) : onClose()); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, abierto]);
  async function ver(c: UltimoConteo) {
    setCargando(c.count_id);
    try { setAbierto(await getConteo(c.count_id)); } catch { /* se queda en la lista */ }
    finally { setCargando(null); }
  }
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label="Historial de conteos">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div data-testid="historial-conteos" className="modal-panel relative flex max-h-[90vh] w-full max-w-2xl flex-col rounded-t-2xl border border-line bg-surface shadow-xl sm:rounded-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <h3 className="flex min-w-0 items-center gap-2 font-display text-lg font-bold text-content">
            {abierto ? (
              <button onClick={() => setAbierto(null)} className="rounded-lg border border-line px-2 py-0.5 text-xs font-medium text-content-2 hover:bg-surface-2">← Historial</button>
            ) : <ClipboardList size={18} className="text-brand-blue" />}
            <span className="truncate">{abierto ? `Conteo del ${fechaLarga(abierto.at)}` : 'Historial de conteos'}</span>
          </h3>
          <button onClick={onClose} className="shrink-0 text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {abierto ? (
            <div className="px-5 py-3" data-testid="historial-detalle">
              <p className="text-sm text-content-2">
                {abierto.by ? <>Contó <b className="text-content">{abierto.by}</b></> : 'Sin usuario'}{abierto.shift_label ? ` · turno ${abierto.shift_label}` : ' · sin turno de caja'} · {abierto.lines_counted} producto{abierto.lines_counted === 1 ? '' : 's'} · {abierto.lines_diff === 0 ? 'todo coincidió' : `${abierto.lines_diff} no coincidía${abierto.lines_diff === 1 ? '' : 'n'}`}
              </p>
              {abierto.note && <p className="mt-1 text-xs text-content-3">Nota: {abierto.note}</p>}
              <ul className="mt-3 divide-y divide-line rounded-xl border border-line">
                {abierto.lines.map((l) => (
                  <li key={l.product_id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <span className="min-w-0 truncate text-content">{l.name}</span>
                    <span className="shrink-0 text-right text-xs text-content-3">
                      {l.expected == null ? 'primer conteo' : `esperaba ${l.expected}`} · contó <b className="text-content">{l.counted}</b>
                      {l.diff != null && l.diff !== 0 && <> <b className={`ml-1 text-sm ${l.diff < 0 ? 'text-amber-600 dark:text-amber-400' : 'text-brand-blue'}`}>{textoDiferencia(l.diff)}</b></>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : lista == null ? (
            <div className="space-y-2 px-5 py-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : lista.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-content-3">Todavía no se ha contado la vitrina ninguna vez.</p>
          ) : (
            <ul className="divide-y divide-line">
              {lista.map((c) => (
                <li key={c.count_id}>
                  <button onClick={() => ver(c)} disabled={cargando === c.count_id} data-testid="historial-fila"
                    className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 px-5 py-2.5 text-left transition hover:bg-surface-2 sm:grid-cols-[11rem_minmax(0,1fr)_auto_auto]">
                    <span className="text-sm font-semibold tabular-nums text-content">{fechaLarga(c.at)}</span>
                    <span className="truncate text-sm text-content-2 sm:order-none">{c.by || 'Sin usuario'}{c.shift_label ? ` · ${c.shift_label}` : ''}</span>
                    <span className="text-xs tabular-nums text-content-3">{c.lines_counted} prod.</span>
                    <span className={`text-right text-xs tabular-nums ${c.lines_diff ? 'text-amber-600 dark:text-amber-400' : 'text-content-3'}`}>{c.lines_diff ? `${c.lines_diff} dif.` : 'coincidió'}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>, document.body);
}

/** REGISTRO DEL ÚLTIMO CONTEO. Con el inventario apagado no pinta nada.
 *  El conteo NO es obligatorio ni al abrir ni al cerrar (Carlos 29-sep-2026): esto no regaña, informa
 *  cuándo fue la última vez y deja contar o ver el historial. Ámbar solo si nunca se ha contado. */
export default function AvisoConteoVitrina({ detalle = false, puedeContar = true, onCambio }: {
  detalle?: boolean;
  puedeContar?: boolean;
  onCambio?: () => void;
}) {
  const [st, setSt] = useState<VitrinaStatus | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [verHist, setVerHist] = useState(false);
  // La lista de «movidos» se congela al abrir la ventana: al guardar, el estado nuevo la trae vacía.
  const [movidosAlAbrir, setMovidosAlAbrir] = useState<number[]>([]);
  useEffect(() => { if (!abierto) setMovidosAlAbrir(st?.moved || []); }, [abierto, st]);

  const cargar = useCallback(() => { getVitrinaStatus().then(setSt).catch(() => setSt(null)); }, []);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    // Antes `abrirConteo` abría la ventana sola al abrir la caja; ya no: solo se recarga el registro.
    const h = () => cargar();
    window.addEventListener(EVENTO_VITRINA, h);
    return () => window.removeEventListener(EVENTO_VITRINA, h);
  }, [cargar]);
  useRefrescoAlVolver(() => cargar(), 120000);

  if (!st || !st.enabled) return null;
  const u = st.last_count;
  const enTurno = !!st.count;
  const nunca = !u;
  return (
    <>
      <div key="aviso" data-testid={nunca ? 'aviso-conteo-vitrina' : 'estado-conteo-vitrina'}
        className={`no-print mb-4 flex flex-wrap items-center gap-3 rounded-xl border px-4 py-2.5 ${nunca ? 'border-amber-500/40 bg-amber-500/10' : 'border-line bg-surface'}`}>
        {nunca ? <AlertTriangle size={18} className="shrink-0 text-amber-500" /> : <ClipboardList size={18} className="shrink-0 text-brand-blue" />}
        <p className="min-w-0 flex-1 text-sm text-content-2">
          {nunca ? (
            <><b className="text-content">La vitrina no se ha contado nunca.</b> Las existencias que ves son las que se cargaron a mano.</>
          ) : (
            <>
              <b className="text-content">Último conteo:</b> {fechaLarga(u.at)}{u.by ? ` · ${u.by}` : ''}{u.shift_label ? ` · ${u.shift_label}` : ''}
              {' · '}{u.lines_counted} producto{u.lines_counted === 1 ? '' : 's'}
              {u.lines_diff > 0 ? <> · <b className="text-amber-600 dark:text-amber-400">{u.lines_diff} no coincidía{u.lines_diff === 1 ? '' : 'n'}</b></> : ' · todo coincidió'}
              {enTurno && <span className="text-content-3"> (este turno)</span>}
            </>
          )}
        </p>
        <div className="flex gap-2">
          {(detalle || !nunca) && (
            <button onClick={() => setVerHist(true)} data-testid="btn-historial-conteos"
              className="whitespace-nowrap rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 transition hover:bg-surface-2">
              Historial
            </button>
          )}
          {puedeContar && (
            <button onClick={() => setAbierto(true)} data-testid="btn-contar"
              className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-semibold transition ${nunca || !enTurno ? 'bg-accent-solid text-on-accent hover:bg-accent-solid/90' : 'border border-line bg-surface text-content-2 hover:bg-surface-2'}`}>
              {enTurno ? 'Volver a contar' : 'Contar ahora'}
            </button>
          )}
        </div>
      </div>
      {abierto && (
        <ConteoVitrinaModal key="conteo" movidos={movidosAlAbrir} turno={st.cashbox?.shift_label}
          onClose={() => setAbierto(false)} onSaved={() => { cargar(); onCambio?.(); }} />
      )}
      {verHist && <HistorialConteosModal key="hist" onClose={() => setVerHist(false)} />}
    </>
  );
}
